import type { FormatterPreset } from "@imminently/decisively-core";
import { formatterLabels, formatterStepOptions, formattersFor } from "../../../utils/formatterMenus";
import type { AttributeOption } from "./types";

export interface ChipDisplayInput {
  attributes: AttributeOption[];
  attributeKey: string;
  formatters: string[];
  customLabel: string;
  /** The deprecated `@parent/...` key the chip was auto-migrated from, if any. */
  migratedFrom?: string;
  /** Whether the chip can be clicked to edit. Changes the hover text. */
  editable: boolean;
  formatterOptionsFor?: (attribute: AttributeOption) => FormatterPreset[];
}

export interface ChipDisplay {
  /** The resolved attribute, or undefined when the key is unknown. */
  attribute: AttributeOption | undefined;
  /** The attribute's real label (or the key when unknown). */
  attributeLabel: string;
  /** The formatter menu for the attribute. */
  formatterOptions: FormatterPreset[];
  /** Props for `AttributeChip`. */
  label: string;
  title: string;
  formatterLabel: string | undefined;
  aliased: boolean;
  unknown: boolean;
  warning: boolean;
}

/**
 * How one attribute placement shows as a chip: label, hover text and flags.
 * Shared by the editor's token node view and the read-only template view, so
 * a chip reads the same in both.
 */
export function describeChip({
  attributes,
  attributeKey,
  formatters,
  customLabel,
  migratedFrom,
  editable,
  formatterOptionsFor,
}: ChipDisplayInput): ChipDisplay {
  const attribute = attributes.find((candidate) => candidate.key === attributeKey);
  const attributeLabel = attribute?.label ?? attributeKey;
  const aliased = customLabel.length > 0;
  const formatterOptions = attribute ? (formatterOptionsFor?.(attribute) ?? formattersFor(attribute.type)) : [];
  // Later steps in a chain also offer the text formatters, so look labels up in that wider set.
  const formatterLabel = formatters.length
    ? formatterLabels(formatters, formatterStepOptions(formatterOptions, 1)).join(" → ")
    : undefined;

  const title = attribute
    ? [
        `${attributeLabel}${aliased ? ` (shown as “${customLabel}”)` : ""}${editable ? " (click to edit)" : ""}`,
        migratedFrom
          ? `Auto-migrated from {{ ${migratedFrom} }}. ${editable ? "Save to keep the change." : "Edit and save to update the stored template."}`
          : "",
      ]
        .filter(Boolean)
        .join("\n")
    : `Unknown attribute: ${attributeKey}`;

  return {
    attribute,
    attributeLabel,
    formatterOptions,
    label: aliased ? customLabel : attributeLabel,
    title,
    formatterLabel,
    aliased,
    unknown: !attribute,
    warning: Boolean(migratedFrom),
  };
}
